/**
 * batch-71 workflow-sjablonen (FO-533 §A3, §A4) - welke stap staat waar.
 *
 * Puur, naar het model van `vereisteStappen` bij de HR-besluiten. De motor leest de
 * stappen, vraagt hier wat er open mag en voert het uit. Een MISLUKTE voorganger
 * houdt zijn opvolgers tegen tot iemand hem oplost; een stap die al open is gaat
 * nooit terug naar wachten als zijn voorganger heropent (er kan al een taak lopen).
 */
import { WORKFLOW_STAP_KLAAR_STATUSSEN, type WorkflowRunStapStatus, type WorkflowRunStatus } from '@streep/types'

export interface StapStand {
  sleutel: string
  status: WorkflowRunStapStatus
  /** Telt mee voor het afronden van de run. */
  verplicht?: boolean
  /** Niet eerder open dan dit moment (uitgestelde stap). */
  opentOp?: Date | null
}

export function isStapKlaarStatus(status: WorkflowRunStapStatus): boolean {
  return (WORKFLOW_STAP_KLAAR_STATUSSEN as readonly WorkflowRunStapStatus[]).includes(status)
}

export interface StapBesluit {
  sleutel: string
  status: WorkflowRunStapStatus
  /** Veranderde de status ten opzichte van de invoer? */
  gewijzigd: boolean
  /** De voorgangers die nog niet klaar zijn (leeg als er niets tegenhoudt). */
  wachtOp: string[]
}

/**
 * Bepaal de status per stap uit de afhankelijkheden. Alleen WACHT_OP_VOORGANGER kan
 * hier veranderen: naar OPEN zodra alle voorgangers klaar zijn en het moment bereikt
 * is. Een voorganger die niet (meer) in de run zit houdt niets tegen: anders blokkeert
 * een sjabloonwijziging lopende runs voor altijd.
 */
export function bepaalStapStatussen(
  stappen: readonly StapStand[],
  afhankelijkheden: Readonly<Record<string, readonly string[]>>,
  nu: Date = new Date(),
): StapBesluit[] {
  const perSleutel = new Map(stappen.map((s) => [s.sleutel, s]))
  return stappen.map((s) => {
    const wachtOp = (afhankelijkheden[s.sleutel] ?? []).filter((v) => {
      const voorganger = perSleutel.get(v)
      return voorganger !== undefined && !isStapKlaarStatus(voorganger.status)
    })
    if (s.status !== 'WACHT_OP_VOORGANGER') return { sleutel: s.sleutel, status: s.status, gewijzigd: false, wachtOp }
    const opTijd = !s.opentOp || s.opentOp.getTime() <= nu.getTime()
    const open = wachtOp.length === 0 && opTijd
    return { sleutel: s.sleutel, status: open ? 'OPEN' : s.status, gewijzigd: open, wachtOp }
  })
}

/** Zijn alle verplichte stappen afgehandeld (afgerond, overgeslagen of n.v.t.)? */
export function zijnVerplichteStappenKlaar(stappen: readonly StapStand[]): boolean {
  return stappen.every((s) => s.verplicht === false || isStapKlaarStatus(s.status))
}

/**
 * De status van een lopende run uit zijn stappen. WACHT = er kan alleen nog iets
 * gebeuren door een externe partij; een mislukte stap houdt de run OPEN (§6.1).
 */
export function runStatusUitStappen(stappen: readonly StapStand[]): Extract<WorkflowRunStatus, 'OPEN' | 'WACHT'> {
  const nietKlaar = stappen.filter((s) => !isStapKlaarStatus(s.status))
  const heeftWerk = nietKlaar.some((s) => s.status === 'OPEN' || s.status === 'BEZIG' || s.status === 'MISLUKT')
  const wachtExtern = nietKlaar.some((s) => s.status === 'WACHT_EXTERN')
  return !heeftWerk && wachtExtern ? 'WACHT' : 'OPEN'
}

export interface StapVoortgang {
  klaar: number
  totaal: number
  open: number
  wachtExtern: number
  mislukt: number
}

export function stapVoortgang(stappen: readonly StapStand[]): StapVoortgang {
  return {
    klaar: stappen.filter((s) => isStapKlaarStatus(s.status)).length,
    totaal: stappen.length,
    open: stappen.filter((s) => s.status === 'OPEN' || s.status === 'BEZIG').length,
    wachtExtern: stappen.filter((s) => s.status === 'WACHT_EXTERN').length,
    mislukt: stappen.filter((s) => s.status === 'MISLUKT').length,
  }
}

/**
 * Vind een fout in de afhankelijkheden: een onbekende voorganger, een stap die op
 * zichzelf wacht of een cirkel. Null = in orde. Gebruikt door de editor (inline op
 * het veld), de start en de drift-test: een cirkel zou een run nooit laten afronden.
 */
export function vindAfhankelijkheidFout(
  stappen: ReadonlyArray<{ code: string; vereisteStappen: readonly string[] }>,
): { code: string; melding: string } | null {
  const codes = new Set(stappen.map((s) => s.code))
  for (const s of stappen) {
    for (const v of s.vereisteStappen) {
      if (v === s.code) return { code: s.code, melding: `Stap ${s.code} kan niet op zichzelf wachten` }
      if (!codes.has(v)) return { code: s.code, melding: `Stap ${s.code} wacht op ${v}, maar die stap bestaat niet` }
    }
  }
  const status = new Map<string, 'bezig' | 'klaar'>()
  const perCode = new Map(stappen.map((s) => [s.code, s]))
  const bezoek = (code: string, pad: string[]): string[] | null => {
    if (status.get(code) === 'klaar') return null
    if (status.get(code) === 'bezig') return [...pad, code]
    status.set(code, 'bezig')
    for (const v of perCode.get(code)?.vereisteStappen ?? []) {
      const cirkel = bezoek(v, [...pad, code])
      if (cirkel) return cirkel
    }
    status.set(code, 'klaar')
    return null
  }
  for (const s of stappen) {
    const cirkel = bezoek(s.code, [])
    if (cirkel) return { code: s.code, melding: `Deze stappen wachten op elkaar: ${cirkel.join(' -> ')}` }
  }
  return null
}
